import React, {FC, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {StyleSheetManager} from 'styled-components';

import {
  buildFrontWindow,
  FrontWindow,
  globalDocument,
  globalWindow,
} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {
  isChrome,
  isElectron,
  popoutRequiresAboutBlank,
  shouldPopout as computeShouldPopout,
  supportsPopout,
} from '../../../../libs/shared/helpers/src/environment/platformHelpers';
import {usePrevious} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {useEventHandler} from '../../../../libs/shared/helpers/src/react/hooks/useEventHandler';
import {useForceUpdate} from '../../../../libs/shared/helpers/src/react/hooks/useForceUpdate';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Layer} from '../../../../libs/shared/ui/src/components/layers/layer';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {SvgManager} from '../../../../libs/shared/ui/src/components/svg/svgManager';
import {CssManager} from '../../../../libs/shared/ui/src/css/cssManager';
import {InteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {GlobalStyles} from '../../../../libs/shared/ui/src/styles/globalStyles';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useUnstrictInitialEffect} from '../../../../srcCommon/helpers/hookHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {startIdleUpdateBlocker} from '../../../services/updater/idleUpdate';
import {CriticalUpdateBanner} from '../../app/banners/criticalUpdate/criticalUpdateBanner';
import {ErrorReporterContainer} from '../../errors/errorReporterContainer';
import {GrammarlyButtonHost} from '../../grammarly/grammarlyButtonHost';
import {GrammarlyProvider} from '../../grammarly/grammarlyProvider';
import {GrammarlyWrapper} from '../../grammarly/grammarlyWrapper';
import {BannersHost} from '../banners/bannersHost';
import {BannersProvider} from '../banners/bannersProvider';
import {Dialog} from '../dialogs/dialog';
import {DialogHeader} from '../dialogs/dialogHeader';
import {DragAndDropProvider} from '../dragAndDropProvider';
import {KeyboardProviderContainer} from '../keyboard/keyboardProviderContainer';
import {FrameContext, FrameContextProps} from './frameContext';
import {frameBannerWidth, FrameProps, StyledFrameBannersDiv, StyledFrameDiv} from './frameHelpers';

/*
 * Component.
 */

interface FrameContentProps extends FrameProps {
  childWindow: FrontWindow | undefined;
  title: string;
  onDialogRequestClose: Handler;
  frameContext: FrameContextProps;
}

const FrameContent: FC<FrameContentProps> = (props) => {
  const {childWindow, title, onDialogRequestClose, onOpenPopout, frameContext, children} = props;

  if (childWindow) {
    // If we have a child window, render inside of it.
    return createPortal(renderContent(props, childWindow), childWindow.document.body);
  }

  // Otherwise, render in a dialog.
  return (
    <Dialog
      {...props}
      onRequestClose={onDialogRequestClose}
      minHeight={props.minHeight || '500px'}
      maxHeight="80vh"
    >
      {!props.shouldHideDialogHeader && (
        <DialogHeader
          {...props}
          onRequestClose={onDialogRequestClose}
          onRequestPopout={supportsPopout() ? onOpenPopout : undefined}
          title={title}
        />
      )}
      <ErrorReporterContainer isTopLevel={false}>
        <FrameContext.Provider value={frameContext}>{children}</FrameContext.Provider>
      </ErrorReporterContainer>
    </Dialog>
  );
};

function renderContent(props: FrameContentProps, childWindow: FrontWindow) {
  const {head} = childWindow.document;
  if (!head) {
    return null;
  }

  return (
    <ErrorReporterContainer isTopLevel={false}>
      <DragAndDropProvider window={childWindow}>
        <StyleSheetManager target={head}>
          <SvgManager>
            <KeyboardProviderContainer>
              <GrammarlyWrapper>
                <Layer window={childWindow}>
                  <CssManager>
                    <FrameContext.Provider value={props.frameContext}>
                      <GlobalStyles />
                      <InteractionParent interactionId={props.interactionId || 'window'}>
                        <StyledFrameDiv
                          $minHeight={props.minHeight}
                          $minWidth={props.minWidth}
                          $maxHeight={props.maxHeight}
                        >
                          <GrammarlyProvider>
                            {props.children}
                            <GrammarlyButtonHost shouldUseBottom />
                          </GrammarlyProvider>
                          <BannersProvider>
                            <StyledFrameBannersDiv>
                              <BannersHost width={frameBannerWidth}>
                                <CriticalUpdateBanner />
                              </BannersHost>
                            </StyledFrameBannersDiv>
                          </BannersProvider>
                        </StyledFrameDiv>
                      </InteractionParent>
                    </FrameContext.Provider>
                  </CssManager>
                </Layer>
              </GrammarlyWrapper>
            </KeyboardProviderContainer>
          </SvgManager>
        </StyleSheetManager>
      </DragAndDropProvider>
    </ErrorReporterContainer>
  );
}

export const ModernFrame: FC<FrameProps> = (props) => {
  const {onRequestClose} = props;
  const {window} = useLayer();
  const theme = useUserTheme();
  const [title, setTitle] = useState('');
  const frameContext: FrameContextProps = useMemo(
    () => ({
      updateTitle: setTitle,
    }),
    [setTitle],
  );

  useInitialEffect(() => {
    const stopIdleUpdateBlocker = startIdleUpdateBlocker();
    return () => stopIdleUpdateBlocker();
  });

  const onDialogRequestClose = useEventHandler(() => onRequestClose(false));

  const childWindow = useChildWindow(props, window);

  useLayoutEffect(() => {
    maybeSetWindowBackgroundColor(childWindow, theme);
    maybeSetWindowTitle(childWindow, title);
  }, [childWindow, theme, title]);

  return (
    <FrameContent
      {...props}
      childWindow={childWindow}
      title={title}
      onDialogRequestClose={onDialogRequestClose}
      frameContext={frameContext}
    />
  );
};

/*
 * Hooks.
 */

interface ChildWindowHandle {
  window: FrontWindow;
  close: Handler;
}

function useChildWindow(props: FrameProps, parentWindow: FrontWindow) {
  const {shouldForcePopout, onRequestClose} = props;
  const childWindowHandleRef = useRef<ChildWindowHandle>();
  const forceUpdate = useForceUpdate();

  /*
   * Child window lifecycle.
   */

  const onBeforeUnload = useEventHandler(async (event) => {
    const isRunningElectron = isElectron();
    if (isRunningElectron) {
      // eslint-disable-next-line no-param-reassign
      event.returnValue = true;
      await delayAsync();
    }

    if (!onRequestClose) {
      return;
    }

    onRequestClose(!isRunningElectron);
  });

  const onUnload = useEventHandler(() => {
    // Remove the child window and re-render.
    childWindowHandleRef.current = undefined;
    forceUpdate();
  });

  // Close the child window when we unmount this component.
  useUnstrictInitialEffect(() => () => {
    if (childWindowHandleRef.current) {
      childWindowHandleRef.current.close();
    }
  });

  /*
   * Global window lifecycle.
   */

  // Setup a listener that closes the popup when we close the global window.
  useInitialEffect(() => {
    const listener = () => {
      const childWindowHandle = childWindowHandleRef.current;
      if (childWindowHandle) {
        childWindowHandle.close();
      }
    };

    globalWindow.addEventListener('beforeunload', listener);
    return () => globalWindow.removeEventListener('beforeunload', listener);
  });

  /*
   * Opening logic.
   */

  const shouldPopout = computeShouldPopout(shouldForcePopout);
  const previousShouldPopout = usePrevious(shouldPopout);
  // If we should open a popout and we didn't have to before, try to
  // open a child window. For, initial renders, we'll try to open a
  // child window if shouldForcePopout is true on the first render.
  const shouldOpenChildWindow = !previousShouldPopout && shouldPopout;

  if (!shouldOpenChildWindow) {
    // We don't need to open a child window. Return the existing one if it exists.
    return childWindowHandleRef.current?.window;
  }

  if (childWindowHandleRef.current) {
    // A child window already exists.
    return childWindowHandleRef.current.window;
  }

  // Open a child window and update the ref.
  const childWindowHandle = openChildWindow(props, parentWindow, onBeforeUnload, onUnload);
  childWindowHandleRef.current = childWindowHandle;

  return childWindowHandle?.window;
}

/*
 * Helpers.
 */

function openChildWindow(
  props: FrameProps,
  parentWindow: FrontWindow,
  onBeforeUnload: HandlerOf<Event>,
  onUnload: Handler,
): ChildWindowHandle | undefined {
  // Create the external window where we'll inject our content.
  const {width} = props;
  const documentHeight = globalDocument.documentElement.clientHeight;
  const safetyMargin = isChrome() ? 30 : 0; // b/c Chrome adds a 30px tall banner with "about:blank" at the top of the window.
  const height = Math.min(
    (props.height || props.startHeight || documentHeight || 500) + safetyMargin,
    screen.height,
  );

  const windowLeft = globalWindow.screenLeft || globalWindow.screenX;
  const windowTop = globalWindow.screenTop || globalWindow.screenY;

  const left = Math.max(0, Math.min(windowLeft + 120, screen.width - width - 50));
  const top = Math.max(0, Math.min(windowTop + 120, screen.height - height - 50));

  // Using window.open('') and window.open('about:blank') should be equivalent, but Electron 4
  // will emit the beforeunload/unload events when using window.open(''), leading us to constantly
  // close and recreate the window. We can't switch to using 'about:blank' entirely because the
  // old Electorn app is depending on the URL being '' when deciding whether it needs to show it
  // in the BrowserWindow 'ready-to-show' event. This should be fixed in Electron 5, so we can go
  // back to always using window.open('') when we no longer have users on the pre-Electron-5
  // desktop app.
  const url = popoutRequiresAboutBlank() ? 'about:blank' : '';
  const openedWindow = parentWindow.open(url, '', `width=${width},height=${height},left=${left},top=${top}`);
  if (!openedWindow) {
    reportToBugsnag(new Error('Unable to open popout window'));
    return undefined;
  }

  const childWindow = buildFrontWindow(openedWindow);
  // Add our element to it and configure the window.
  childWindow.document.title = 'Front';
  childWindow.addEventListener('beforeunload', onBeforeUnload);
  childWindow.addEventListener('unload', onUnload);

  const onClose = () => {
    childWindow.removeEventListener('beforeunload', onBeforeUnload);
    childWindow.removeEventListener('unload', onUnload);
    childWindow.close();
  };

  // Make relative URLs work in the external window.
  const baseElement = globalDocument.createElement('base');
  baseElement.setAttribute('href', `${globalWindow.location.protocol}//${globalWindow.location.host}`);
  if (childWindow.document.head) {
    childWindow.document.head.appendChild(baseElement);
  }

  return {
    window: childWindow,
    close: onClose,
  };
}

function maybeSetWindowBackgroundColor(childWindow: FrontWindow | undefined, theme: UserTheme) {
  if (!childWindow) {
    return;
  }

  // Sets the background color of the window's body. When the popup window is closed, it briefly flashes
  // the page's background as the application unloads. This causes a white flash, which is jarring in
  // dark mode.
  childWindow.document.body.style.setProperty('background', theme.appBackground);
}

function maybeSetWindowTitle(childWindow: FrontWindow | undefined, title: string) {
  if (!childWindow) {
    return;
  }

  // eslint-disable-next-line no-param-reassign
  childWindow.document.title = title.slice(0, 100);
}
